import { create } from "zustand";

/**
 * The ONLY Zustand store (contract §7.7).
 *
 * Genuine client state only: which branch the UI is pointed at, whether the
 * mobile nav is open, and whether the tenant is suspended. No lead, member,
 * branch or import data lives here — that is TanStack Query's job.
 */

const BRANCH_STORAGE_KEY = "gk.activeBranchId";

/**
 * "All branches" is stored as the literal string "all", not as an absent key.
 *
 * `null` has to mean two different things — "the owner deliberately chose the
 * cross-branch rollup" and "this browser has never chosen anything" — and
 * collapsing them made the rollup unreachable: `useSyncActiveBranch` saw
 * `activeBranchId === null`, read it as "not chosen yet" and immediately put the
 * default branch back, so the item in the switcher did nothing.
 */
const ALL_BRANCHES = "all";

interface StoredBranch {
  /** null = "all branches". */
  id: number | null;
  /** false when this browser has never made a choice. */
  initialised: boolean;
}

function readStoredBranch(): StoredBranch {
  try {
    const raw = window.localStorage.getItem(BRANCH_STORAGE_KEY);
    if (!raw) return { id: null, initialised: false };
    if (raw === ALL_BRANCHES) return { id: null, initialised: true };
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0
      ? { id: parsed, initialised: true }
      : { id: null, initialised: false };
  } catch {
    // Private mode / disabled storage: fall back to "no preference".
    return { id: null, initialised: false };
  }
}

function writeStoredBranchId(id: number | null): void {
  try {
    window.localStorage.setItem(BRANCH_STORAGE_KEY, id === null ? ALL_BRANCHES : String(id));
  } catch {
    // Non-fatal: the selection still holds for this tab.
  }
}

export interface AppState {
  /** null = "all branches I can see". Persisted to localStorage. */
  activeBranchId: number | null;
  /**
   * Whether `activeBranchId` reflects a deliberate choice.
   *
   * `false` only until the first selection (explicit or the sign-in fallback).
   * `useSyncActiveBranch` applies its default ONLY while this is false, which is
   * what makes a deliberate `null` ("all branches") stick.
   */
  branchInitialised: boolean;
  setActiveBranchId: (id: number | null) => void;

  /** Mobile nav sheet / desktop sidebar rail. */
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  /** Set by the api interceptor on a 402; renders the full-screen block. */
  tenantSuspended: boolean;
  suspensionReason: string;
  setTenantSuspended: (suspended: boolean, reason?: string) => void;
}

const storedBranch = readStoredBranch();

export const useAppStore = create<AppState>((set) => ({
  activeBranchId: storedBranch.id,
  branchInitialised: storedBranch.initialised,
  setActiveBranchId: (id) => {
    writeStoredBranchId(id);
    set({ activeBranchId: id, branchInitialised: true });
  },

  sidebarOpen: false,
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  tenantSuspended: false,
  suspensionReason: "",
  setTenantSuspended: (suspended, reason = "") =>
    set({ tenantSuspended: suspended, suspensionReason: suspended ? reason : "" }),
}));

/** Selector helpers keep components from re-rendering on unrelated store writes. */
export const selectActiveBranchId = (s: AppState) => s.activeBranchId;
export const selectBranchInitialised = (s: AppState) => s.branchInitialised;
export const selectSidebarOpen = (s: AppState) => s.sidebarOpen;
